// frontend/src/pages/admin/StockLevel.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiSearch,
  FiPackage,
  FiAlertCircle,
  FiEye,
  FiTrendingUp,
  FiTrendingDown,
  FiX
} from 'react-icons/fi';

const StockLevel = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
    const [summary, setSummary] = useState({
    total_products: 0,
    normal_stock: 0,
    low_stock: 0,
    out_of_stock: 0,
    total_value: 0
    });
  const [categories, setCategories] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productHistory, setProductHistory] = useState([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  // ========================================
  // FETCH DATA
  // ========================================
  useEffect(() => {
    fetchStockLevel();
    fetchCategories();
  }, [pagination.page, searchQuery, selectedCategory, lowStockOnly]);

  const fetchStockLevel = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/inventory/stock-level', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          category_id: selectedCategory,
          low_stock: lowStockOnly
        }
      });

      if (response.data.success) {
        setProducts(response.data.data.products);
        setSummary(response.data.data.summary);
        setPagination(response.data.data.pagination);
        }
    } catch (error) {
      console.error('Fetch stock level error:', error);
      toast.error('Үлдэгдэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/products/categories/list');
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const fetchProductHistory = async (productId) => {
    try {
      const response = await axios.get('/api/inventory/history', {
        params: {
          product_id: productId,
          limit: 20
        }
      });

      if (response.data.success) {
        setProductHistory(response.data.data.history);
      }
    } catch (error) {
      console.error('Fetch history error:', error);
      toast.error('Түүх татахад алдаа гарлаа');
    }
  };

  const viewHistory = async (product) => {
    setSelectedProduct(product);
    await fetchProductHistory(product.id);
    setShowHistoryModal(true);
  };

  // ========================================
  // HANDLERS
  // ========================================
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleCategoryFilter = (e) => {
    setSelectedCategory(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const toggleLowStock = () => {
    setLowStockOnly(!lowStockOnly);
    setPagination({ ...pagination, page: 1 });
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) {
      return { label: 'Дууссан', color: 'red', icon: FiAlertCircle };
    } else if (quantity < 10) {
      return { label: 'Бага', color: 'yellow', icon: FiAlertCircle };
    } else {
      return { label: 'Хэвийн', color: 'green', icon: FiPackage };
    }
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Барааны үлдэгдэл</h2>
        <p className="text-gray-600 text-sm mt-1">Агуулах дахь бараануудын одоогийн үлдэгдэл</p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Баркод эсвэл нэрээр хайх..."
                className="input pl-10"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={handleCategoryFilter}
              className="input"
            >
              <option value="">Бүх ангилал</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Low Stock Toggle */}
          <div>
            <button
              onClick={toggleLowStock}
              className={`btn w-full ${
                lowStockOnly 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'btn-secondary'
              }`}
            >
              <FiAlertCircle className="mr-2" />
              {lowStockOnly ? 'Бага үлдэгдэл' : 'Бүгд харах'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-semibold">Нийт бараа</p>
              <p className="text-2xl font-bold text-blue-900">{summary.total_products}</p>
            </div>
            <FiPackage className="text-blue-600" size={32} />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-semibold">Хэвийн үлдэгдэл</p>
              <p className="text-2xl font-bold text-green-900">
                {products.filter(p => p.stock_quantity >= 10).length}
              </p>
            </div>
            <FiTrendingUp className="text-green-600" size={32} />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-semibold">Бага үлдэгдэл</p>
              <p className="text-2xl font-bold text-yellow-900">
                {products.filter(p => p.stock_quantity > 0 && p.stock_quantity < 10).length}
              </p>
            </div>
            <FiAlertCircle className="text-yellow-600" size={32} />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-red-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-semibold">Дууссан</p>
              <p className="text-2xl font-bold text-red-900">
                {products.filter(p => p.stock_quantity === 0).length}
              </p>
            </div>
            <FiTrendingDown className="text-red-600" size={32} />
          </div>
        </div>
      </div>

      {/* Products List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner-lg"></div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-th">Баркод</th>
                <th className="table-th">Барааны нэр</th>
                <th className="table-th">Ангилал</th>
                <th className="table-th text-right">Үлдэгдэл</th>
                <th className="table-th text-right">Өртөг</th>
                <th className="table-th text-right">Үнэ</th>
                <th className="table-th">Төлөв</th>
                <th className="table-th text-right">Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-500">
                    <FiPackage size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Бараа олдсонгүй</p>
                  </td>
                </tr>
              ) : (
                products.map(product => {
                  const status = getStockStatus(product.stock_quantity);
                  const StatusIcon = status.icon;

                  return (
                    <tr key={product.id} className="table-row">
                      <td className="table-td">
                        <span className="font-mono text-sm">{product.barcode}</span>
                      </td>
                      <td className="table-td">
                        <p className="font-semibold text-gray-900">{product.name}</p>
                      </td>
                      <td className="table-td">
                        <span className="badge badge-info">{product.category_name}</span>
                      </td>
                      <td className="table-td text-right">
                        <span className={`font-bold text-${status.color}-600`}>
                          {product.stock_quantity}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">{product.unit}</span>
                      </td>
                      <td className="table-td text-right">
                        {product.cost_price.toLocaleString()}₮
                      </td>
                      <td className="table-td text-right font-semibold">
                        {product.retail_price.toLocaleString()}₮
                      </td>
                      <td className="table-td">
                        <span className={`badge badge-${status.color} flex items-center w-fit`}>
                          <StatusIcon className="mr-1" size={14} />
                          {status.label}
                        </span>
                      </td>
                      <td className="table-td text-right">
                        <button
                          onClick={() => viewHistory(product)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Хөдөлгөөний түүх"
                        >
                          <FiEye size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination-container">
          <p className="pagination-info">
            Нийт {pagination.total} бараа
          </p>
          <div className="pagination-controls">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              Өмнөх
            </button>
            <span className="pagination-page-info">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.pages}
              className="pagination-button"
            >
              Дараах
            </button>
          </div>
        </div>
      )}

      {/* PRODUCT HISTORY MODAL */}
      {showHistoryModal && selectedProduct && (
        <ProductHistoryModal
          product={selectedProduct}
          history={productHistory}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

// ========================================
// PRODUCT HISTORY MODAL
// ========================================
const ProductHistoryModal = ({ product, history, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content max-w-4xl">
      <div className="modal-header">
        <h3 className="modal-title">
          📊 Барааны хөдөлгөөн - {product.name}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={24} />
        </button>
      </div>

      <div className="modal-body">
        {/* Product Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b">
          <div>
            <p className="text-sm text-gray-600">Баркод</p>
            <p className="font-semibold font-mono">{product.barcode}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Ангилал</p>
            <p className="font-semibold">{product.category_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Үлдэгдэл</p>
            <p className={`text-xl font-bold ${
              product.stock_quantity < 10 ? 'text-red-600' : 'text-green-600'
            }`}>
              {product.stock_quantity} {product.unit}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Үнэ</p>
            <p className="text-xl font-bold text-gray-900">
              {product.retail_price.toLocaleString()}₮
            </p>
          </div>
        </div>

        {/* History */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">
            Хөдөлгөөний түүх ({history.length})
          </h4>
          
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              <p>Хөдөлгөөн олдсонгүй</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Огноо</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Баримт №</th>
                    <th className="px-4 py-2 text-center text-sm font-semibold">Төрөл</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold">Тоо ширхэг</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold">Үнэ</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold">Дүн</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item, index) => {
                    const isIn = item.transaction_type === 'IN';
                    
                    return (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-3 text-sm">
                          {item.transaction_date}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-sm font-semibold ${
                            isIn ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {item.transaction_number}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isIn ? (
                            <span className="badge badge-green flex items-center justify-center w-fit mx-auto">
                              <FiTrendingUp className="mr-1" size={12} />
                              Орлого
                            </span>
                          ) : (
                            <span className="badge badge-red flex items-center justify-center w-fit mx-auto">
                              <FiTrendingDown className="mr-1" size={12} />
                              Зарлага
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold ${
                            isIn ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {isIn ? '+' : ''}{item.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {item.unit_price?.toLocaleString()}₮
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {item.total_price?.toLocaleString()}₮
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer">
        <button onClick={onClose} className="btn btn-secondary w-full">
          Хаах
        </button>
      </div>
    </div>
  </div>
);

export default StockLevel;